import customerRideApi from 'api/customerRideApi';
import ToastHelper from 'general/helpers/ToastHelper';
import Utils from 'general/utils/Utils';
import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import RideAppIcon from './RideAppIcon';
import RidePortal from './RidePortal';
import {
  customerCancelReasons,
  formatRideDateTime,
  formatRideMoney,
  formatRideAddress,
  rideErrorMessage,
  rideStatusMeta,
  rideTripStatusLabel,
} from '../rideUtils';

const RideBookingDetailModal = ({ bookingId, onClose, onChanged }) => {
  const [booking, setBooking] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [reasonCode, setReasonCode] = useState('CHANGE_OF_PLANS');
  const [reasonDetails, setReasonDetails] = useState('');
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [review, setReview] = useState(null);
  const [showDriverAvatarModal, setShowDriverAvatarModal] = useState(false);
  const loadInFlightRef = useRef(false);

  const load = async ({ silent = false } = {}) => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    if (!silent) setLoading(true);
    try {
      const response = await customerRideApi.getBooking(bookingId);
      setBooking(response.booking);
      setError('');
      if (response.booking?.tripStatus === 'IN_PROGRESS') {
        const progressResponse = await customerRideApi.getTripProgress(bookingId);
        setProgress(progressResponse);
      }
      if (response.booking?.tripStatus === 'COMPLETED' && response.booking?.status === 'CONFIRMED') {
        const reviewResponse = await customerRideApi.getReview(bookingId);
        setReview(reviewResponse.review || null);
        if (reviewResponse.review) {
          setRating(reviewResponse.review.rating);
          setComment(reviewResponse.review.comment || '');
        }
      }
    } catch (requestError) {
      if (!silent) setError(rideErrorMessage(requestError));
    } finally {
      loadInFlightRef.current = false;
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [bookingId]);

  useEffect(() => {
    if (!booking) return undefined;
    const isTerminal = ['CANCELLED', 'REJECTED', 'EXPIRED', 'PICKUP_FAILED'].includes(booking.status)
      || ['COMPLETED', 'CANCELLED', 'EXPIRED', 'DRIVER_NO_SHOW'].includes(booking.tripStatus);
    if (isTerminal) return undefined;

    const timer = window.setInterval(() => load({ silent: true }), 5000);
    return () => window.clearInterval(timer);
  }, [bookingId, booking?.status, booking?.tripStatus]);

  const cancel = async () => {
    if (reasonCode === 'OTHER' && (reasonDetails.trim().length < 5 || reasonDetails.trim().length > 500)) {
      setError('Lý do khác phải có từ 5 đến 500 ký tự.');
      return;
    }
    setWorking(true);
    setError('');
    try {
      const response = await customerRideApi.cancelBooking(bookingId, {
        reasonCode,
        ...(reasonCode === 'OTHER' ? { reasonDetails: reasonDetails.trim() } : {}),
      });
      setBooking(response.booking);
      setShowCancel(false);
      ToastHelper.showSuccess('Đã hủy đặt chỗ.');
      onChanged();
    } catch (requestError) {
      setError(rideErrorMessage(requestError));
    } finally {
      setWorking(false);
    }
  };

  const submitReview = async () => {
    setWorking(true);
    setError('');
    try {
      const response = await customerRideApi.submitReview(bookingId, { rating: Number(rating), comment: comment.trim() });
      setReview(response.review);
      ToastHelper.showSuccess('Đã lưu đánh giá chuyến đi.');
    } catch (requestError) {
      setError(rideErrorMessage(requestError));
    } finally {
      setWorking(false);
    }
  };

  const handleCopy = (text, label = 'Mã') => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    ToastHelper.showSuccess(`Đã sao chép ${label}!`);
  };

  const paymentLabel = (method) => {
    switch (method) {
      case 'BIDV':
        return 'Chuyển khoản BIDV';
      case 'WALLET':
        return 'Ví Xe ghép';
      case 'CASH':
      default:
        return 'Tiền mặt';
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showDriverAvatarModal) {
        setShowDriverAvatarModal(false);
      }
    };
    if (showDriverAvatarModal) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDriverAvatarModal]);

  const awaitingBidv = booking?.paymentMethod === 'BIDV' && booking?.paymentStatus === 'PENDING';
  const status = awaitingBidv ? { label: 'Chờ thanh toán BIDV', tone: 'warning' } : rideStatusMeta(booking?.status);
  const canCancel = ['PENDING_DRIVER', 'CONFIRMED'].includes(booking?.status) && booking?.tripStatus === 'PUBLISHED';
  const canReview = booking?.status === 'CONFIRMED' && booking?.tripStatus === 'COMPLETED';
  const payable = Math.max(0, Number(booking?.totalFare || 0) - Number(booking?.promoDiscountAmount || 0));

  const getTimelineStep = () => {
    if (!booking) return 0;
    if (booking.tripStatus === 'COMPLETED' || booking.droppedOffAt) return 4;
    if (booking.pickedUpAt) return 3;
    if (booking.tripStatus === 'IN_PROGRESS') return 2;
    if (booking.status === 'CONFIRMED') return 1;
    return 0;
  };

  const currentStep = getTimelineStep();
  const isFailedOrCancelled = ['CANCELLED', 'REJECTED', 'EXPIRED', 'PICKUP_FAILED'].includes(booking?.status)
    || ['CANCELLED', 'EXPIRED', 'DRIVER_NO_SHOW'].includes(booking?.tripStatus);

  const driverName = booking?.driver?.fullname || booking?.driverName;
  const driverPhone = booking?.driver?.phone || booking?.driverPhone;
  const rawDriverAvatar = booking?.driver?.avatarUrl || booking?.driverAvatarUrl || booking?.driver?.avatar;
  const driverAvatar = rawDriverAvatar ? Utils.getFullUrl(rawDriverAvatar) : null;
  const driverRating = booking?.driver?.ratingAverage ?? booking?.driverRating;
  const vehicleName = booking?.vehicle?.name || booking?.vehicleName || (booking?.vehicle?.brand ? `${booking?.vehicle?.brand} ${booking?.vehicle?.model || ''}`.trim() : null);
  const vehiclePlate = booking?.vehicle?.plate || booking?.vehiclePlate;

  const pickup = formatRideAddress(booking?.pickup?.address);
  const dropoff = formatRideAddress(booking?.dropoff?.address);

  const timelineSteps = [
    { label: awaitingBidv ? 'Chờ thanh toán' : 'Chờ xác nhận', desc: booking?.holdExpiresAt ? `Giữ chỗ đến ${formatRideDateTime(booking.holdExpiresAt)}` : 'Chờ tài xế nhận' },
    { label: 'Đã xác nhận', desc: driverName ? `Tài xế ${driverName}` : 'Đã xác nhận chỗ' },
    { label: 'Khởi hành', desc: booking?.tripStatus === 'IN_PROGRESS' ? 'Chuyến xe đang chạy' : 'Chờ xe xuất phát' },
    { label: 'Đã đón khách', desc: booking?.pickedUpAt ? `Đón lúc ${formatRideDateTime(booking.pickedUpAt)}` : 'Chờ đón tại điểm hẹn' },
    { label: 'Đã đến nơi', desc: booking?.droppedOffAt ? `Trả lúc ${formatRideDateTime(booking.droppedOffAt)}` : 'Hoàn thành chuyến' },
  ];

  return (
    <RidePortal>
      <div className="customer-ride-modal-backdrop" role="presentation" onMouseDown={onClose}>
        <div
          className="customer-ride-modal customer-ride-detail-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Chi tiết đặt chỗ"
          onMouseDown={(event) => event.stopPropagation()}
        >
          {/* 1. Header Bar */}
          <div className="customer-ride-modal-head-bar">
            <div>
              <span className="customer-ride-eyebrow">CHUYẾN ĐI ĐÃ ĐẶT</span>
              <h2 className="modal-title">Chi tiết đặt chỗ</h2>
            </div>
            <button className="customer-ride-modal-close-btn" type="button" onClick={onClose} aria-label="Đóng">
              <i className="fa-solid fa-xmark" />
            </button>
          </div>

          {loading ? (
            <div className="customer-ride-loading">
              <RideAppIcon name="time" className="ride-icon-spin" /> Đang tải chi tiết...
            </div>
          ) : error && !booking ? (
            <div className="customer-ride-alert danger">{error}</div>
          ) : booking && (
            <div className="customer-ride-modal-scroll-body">
              {/* 2. Status Banner */}
              <div className="customer-ride-status-banner">
                <div className="status-banner-left">
                  <span className={`status-pill ${status.tone}`}>
                    <span className="status-dot" />
                    {status.label}
                  </span>
                  <span className="demand-type-tag">
                    <i className="fa-solid fa-car" /> {booking.isCharter ? 'Bao xe EVG' : 'Xe ghép EVG'}
                  </span>
                </div>
                <span className="demand-date-label">
                  Khởi hành: {formatRideDateTime(booking.tripDepartureAt)}
                </span>
              </div>

              {awaitingBidv && (
                <section className="ride-bidv-payment-card">
                  <div className="ride-bidv-amount"><span>Tổng thanh toán</span><strong>{formatRideMoney(payable, booking.currency || 'VND')}</strong></div>
                  {booking.bidvPayment?.qrCode
                    ? <QRCode value={String(booking.bidvPayment.qrCode)} size={190} />
                    : <div className="ride-bidv-qr-placeholder"><RideAppIcon name="warning" /> Chưa tạo được mã QR BIDV</div>}
                  <div className="ride-bidv-bank-info">
                    <strong>Ngân hàng BIDV</strong>
                    {booking.bidvPayment?.accountNumber && <p><span>Số tài khoản</span><b>{booking.bidvPayment.accountNumber}</b></p>}
                    {booking.bidvPayment?.accountName && <p><span>Chủ tài khoản</span><b>{booking.bidvPayment.accountName}</b></p>}
                    <p><span>Mã đặt chỗ</span><b>{booking.bookingId}</b></p>
                  </div>
                  <p className="ride-bidv-payment-hint"><RideAppIcon name="time" className="ride-icon-spin" /> Đang chờ BIDV xác nhận. Chuyến chưa được gửi tới tài xế.</p>
                  <button type="button" className="customer-ride-secondary full" onClick={() => load()}>Tôi đã chuyển khoản · Kiểm tra lại</button>
                </section>
              )}

              {/* 3. Progress Card (If in progress) */}
              {progress && (
                <div className="customer-ride-section-card progress-card">
                  <div className="progress-badge">
                    <i className="fa-solid fa-location-arrow" />
                    {progress.ownDroppedOff
                      ? 'Bạn đã xuống xe'
                      : progress.ownPickedUp
                        ? 'Bạn đang trên xe'
                        : 'Đang chờ tài xế đón bạn'}
                  </div>
                  <div className="progress-summary">
                    {progress.pendingStops > 0
                      ? `Đón ${progress.pickedUpCount}/${progress.totalPassengerBookings} · Trả ${progress.droppedOffCount}/${progress.totalPassengerBookings} · Còn ${progress.pendingStops} điểm dừng`
                      : `Đã đón & trả đủ ${progress.totalPassengerBookings} khách`}
                  </div>
                </div>
              )}

              {/* 4. Timeline or Cancellation Alert */}
              {isFailedOrCancelled ? (
                <div className="customer-ride-failed-card">
                  <i className="fa-solid fa-circle-xmark failed-icon" />
                  <div className="failed-content">
                    <strong>
                      {booking.status === 'REJECTED'
                        ? 'Tài xế đã từ chối yêu cầu đặt chỗ này'
                        : booking.status === 'EXPIRED'
                          ? 'Yêu cầu đã hết hạn giữ ghế'
                          : booking.status === 'PICKUP_FAILED'
                            ? 'Tài xế không đón được bạn'
                            : booking.tripStatus === 'DRIVER_NO_SHOW'
                              ? 'Tài xế không đến điểm hẹn'
                              : 'Chuyến đi đã bị hủy'}
                    </strong>
                    {(booking.cancellationReason || booking.driverResponseReason || booking.pickupFailureReason) && (
                      <p>Lý do: {booking.cancellationReason || booking.driverResponseReason || booking.pickupFailureReason}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="customer-ride-section-card timeline-card">
                  <div className="section-card-title">
                    <i className="fa-solid fa-bars-staggered" /> Trạng thái chuyến đi
                  </div>
                  <div className="customer-ride-timeline-steps">
                    {timelineSteps.map((step, idx) => {
                      const isDone = idx < currentStep;
                      const isCurrent = idx === currentStep;
                      const stepClass = isDone ? 'step-done' : isCurrent ? 'step-current' : 'step-pending';
                      return (
                        <div key={step.label} className={`customer-ride-timeline-step ${stepClass}`}>
                          <div className="customer-ride-timeline-node">
                            {isDone ? <i className="fa-solid fa-check" /> : <span>{idx + 1}</span>}
                          </div>
                          <span className="customer-ride-timeline-label">{step.label}</span>
                          <small className="customer-ride-timeline-desc">{step.desc}</small>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 5. Route Card */}
              <div className="customer-ride-section-card route-card">
                <div className="section-card-title">
                  <i className="fa-solid fa-route" /> Lộ trình di chuyển
                </div>
                <div className="route-timeline">
                  <div className="route-stop pickup-stop">
                    <div className="stop-marker pickup-marker">
                      <i className="fa-regular fa-circle-dot" />
                    </div>
                    <div className="stop-info">
                      <span className="stop-label">Điểm đón</span>
                      <strong className="stop-primary">{pickup.primary}</strong>
                      {pickup.secondary && <span className="stop-secondary">{pickup.secondary}</span>}
                    </div>
                  </div>
                  <div className="route-connector-line" />
                  <div className="route-stop dropoff-stop">
                    <div className="stop-marker dropoff-marker">
                      <i className="fa-solid fa-location-dot" />
                    </div>
                    <div className="stop-info">
                      <span className="stop-label">Điểm trả</span>
                      <strong className="stop-primary">{dropoff.primary}</strong>
                      {dropoff.secondary && <span className="stop-secondary">{dropoff.secondary}</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* 6. Driver & Vehicle Card */}
              {(driverName || vehiclePlate || vehicleName) && (
                <div className="customer-ride-section-card driver-card">
                  <div className="section-card-title">
                    <i className="fa-solid fa-id-card" /> Thông tin tài xế & phương tiện
                  </div>
                  <div className="driver-card-body">
                    <div
                      className="driver-avatar-box clickable"
                      onClick={() => setShowDriverAvatarModal(true)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setShowDriverAvatarModal(true);
                        }
                      }}
                      title="Bấm để xem chi tiết ảnh tài xế"
                    >
                      {driverAvatar ? (
                        <img
                          src={driverAvatar}
                          alt={driverName || 'Tài xế'}
                          className="driver-avatar-img"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const placeholder = e.currentTarget.parentElement?.querySelector('.driver-avatar-placeholder');
                            if (placeholder) placeholder.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="driver-avatar-placeholder"
                        style={{ display: driverAvatar ? 'none' : 'flex' }}
                      >
                        <i className="fa-regular fa-user" />
                      </div>
                      <span className="driver-avatar-zoom-overlay">
                        <i className="fa-solid fa-magnifying-glass-plus" />
                      </span>
                    </div>
                    <div className="driver-details">
                      <div className="driver-name-row">
                        <strong className="driver-fullname">{driverName || 'Tài xế EVG'}</strong>
                        {driverRating !== null && driverRating !== undefined && (
                          <span className="driver-rating">
                            <i className="fa-solid fa-star" /> {Number(driverRating).toFixed(1)}
                          </span>
                        )}
                      </div>
                      <div className="driver-vehicle-row">
                        {vehicleName && (
                          <span className="vehicle-badge">
                            <i className="fa-solid fa-car-side" /> {vehicleName}
                          </span>
                        )}
                        {vehiclePlate && (
                          <span className="plate-badge">{vehiclePlate}</span>
                        )}
                      </div>
                    </div>
                    {driverPhone ? (
                      <a
                        href={`tel:${driverPhone}`}
                        className="driver-call-btn"
                        title={`Gọi tài xế: ${driverPhone}`}
                      >
                        <i className="fa-solid fa-phone" />
                        <span>Gọi điện</span>
                      </a>
                    ) : (
                      <span className="driver-phone-hint">SĐT hiện khi xác nhận</span>
                    )}
                  </div>
                </div>
              )}

              {/* 7. Trip Specs Card */}
              <div className="customer-ride-section-card specs-card">
                <div className="section-card-title">
                  <i className="fa-solid fa-circle-info" /> Chi tiết đặt chỗ
                </div>
                <div className="specs-list">
                  <div className="spec-item">
                    <span className="spec-label">Mã chuyến</span>
                    <span className="spec-value bold">{booking.tripCode || 'Chuyến xe ghép EVG'}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Thời gian xuất phát</span>
                    <span className="spec-value">{formatRideDateTime(booking.tripDepartureAt)}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Dịch vụ</span>
                    <span className="spec-value">{booking.isCharter ? 'Bao xe EVG' : 'Xe ghép EVG'}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Số ghế đặt</span>
                    <span className="spec-value bold">
                      {booking.isCharter
                        ? `${booking.charterVehicleCapacity || 4} chỗ (Bao xe)`
                        : `${booking.seats} ghế`}
                    </span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Trạng thái chuyến</span>
                    <span className="spec-value">{rideTripStatusLabel(booking.tripStatus)}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Phương thức thanh toán</span>
                    <span className="spec-value">{paymentLabel(booking.paymentMethod)}</span>
                  </div>
                  <div className="spec-item spec-with-copy">
                    <span className="spec-label">Mã đặt chỗ</span>
                    <div className="spec-copy-val">
                      <code className="demand-id-code">{booking.bookingId}</code>
                      <button
                        type="button"
                        className="mini-copy-btn"
                        onClick={() => handleCopy(booking.bookingId, 'Mã đặt chỗ')}
                      >
                        <i className="fa-regular fa-copy" /> Sao chép
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 8. Fare Breakdown Card */}
              <div className="customer-ride-section-card fare-card">
                <div className="section-card-title">
                  <i className="fa-solid fa-receipt" /> Cước, phí chuyến đi
                </div>
                <div className="fare-breakdown">
                  <div className="fare-row">
                    <span>{booking.isCharter ? 'Giá bao xe' : `Giá vé (${booking.seats} ghế)`}</span>
                    <span>{formatRideMoney(booking.totalFare, booking.currency)}</span>
                  </div>
                  {Number(booking.detourSurcharge || 0) > 0 && (
                    <div className="fare-row">
                      <span>Phụ phí điểm đón/trả lệch tuyến</span>
                      <span>+{formatRideMoney(booking.detourSurcharge, booking.currency)}</span>
                    </div>
                  )}
                  {Number(booking.extraSurcharge || 0) > 0 && (
                    <div className="fare-row">
                      <span>Phụ phí khác</span>
                      <span>+{formatRideMoney(booking.extraSurcharge, booking.currency)}</span>
                    </div>
                  )}
                  {Number(booking.promoDiscountAmount || 0) > 0 && (
                    <div className="fare-row promo-row">
                      <span>Mã khuyến mại {booking.promoCode ? `"${booking.promoCode}"` : ''}</span>
                      <span className="discount-val">
                        -{formatRideMoney(booking.promoDiscountAmount, booking.currency)}
                      </span>
                    </div>
                  )}
                  <div className="fare-divider" />
                  <div className="fare-row total-row">
                    <strong>Tổng tiền thanh toán</strong>
                    <strong className="total-amount">
                      {formatRideMoney(payable, booking.currency)}
                    </strong>
                  </div>
                </div>
              </div>

              {/* 9. Customer Note if any */}
              {booking.customerNote && (
                <div className="customer-ride-note-box">
                  <div className="note-title">
                    <i className="fa-regular fa-message" /> Ghi chú cho tài xế
                  </div>
                  <p>{booking.customerNote}</p>
                </div>
              )}

              {/* 10. Review Card if completed */}
              {canReview && (
                <div className="customer-ride-section-card review-card">
                  <div className="section-card-title">
                    <i className="fa-solid fa-star" /> {review ? 'Đánh giá của bạn' : 'Đánh giá chuyến đi'}
                  </div>
                  <div className="customer-ride-review-box-content">
                    {review ? (
                      <>
                        <div className="customer-ride-stars" aria-label={`Đã đánh giá ${review.rating} sao`}>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span key={star} className={`star-btn readonly ${star <= review.rating ? 'active' : ''}`}>
                              <i className={star <= review.rating ? 'fa-solid fa-star' : 'fa-regular fa-star'} />
                            </span>
                          ))}
                          <span className="rating-desc">
                            {review.rating === 5 ? 'Tuyệt vời' : review.rating === 4 ? 'Hài lòng' : review.rating === 3 ? 'Bình thường' : review.rating === 2 ? 'Không hài lòng' : 'Rất tệ'}
                          </span>
                        </div>
                        {review.comment && <div className="review-comment-readonly">{review.comment}</div>}
                        <div className="review-submitted-note">
                          <i className="fa-solid fa-circle-check" /> Bạn đã gửi đánh giá cho chuyến đi này.
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="customer-ride-stars">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              type="button"
                              key={star}
                              className={`star-btn ${star <= rating ? 'active' : ''}`}
                              onClick={() => setRating(star)}
                              aria-label={`${star} sao`}
                            >
                              <i className={star <= rating ? 'fa-solid fa-star' : 'fa-regular fa-star'} />
                            </button>
                          ))}
                          <span className="rating-desc">
                            {rating === 5 ? 'Tuyệt vời' : rating === 4 ? 'Hài lòng' : rating === 3 ? 'Bình thường' : rating === 2 ? 'Không hài lòng' : 'Rất tệ'}
                          </span>
                        </div>
                        <textarea
                          className="review-textarea"
                          maxLength={200}
                          value={comment}
                          onChange={(event) => setComment(event.target.value)}
                          placeholder="Chia sẻ trải nghiệm của bạn về chuyến đi..."
                          rows={3}
                        />
                        <button
                          className="customer-ride-primary review-submit-btn"
                          type="button"
                          disabled={working}
                          onClick={submitReview}
                        >
                          {working ? <><i className="fa-solid fa-spinner fa-spin" /> Đang gửi...</> : 'Gửi đánh giá'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Error Alert */}
              {error && <div className="customer-ride-alert danger">{error}</div>}

              {/* 11. Cancellation Action & Box */}
              {canCancel && !showCancel && (
                <button
                  className="customer-ride-secondary danger full"
                  type="button"
                  onClick={() => setShowCancel(true)}
                >
                  <i className="fa-regular fa-circle-xmark" /> Hủy đặt chỗ
                </button>
              )}

              {showCancel && (
                <div className="customer-ride-cancel-box">
                  <h3>
                    <i className="fa-solid fa-triangle-exclamation" /> Lý do hủy chuyến
                  </h3>
                  <p className="cancel-subtitle">Vui lòng chọn lý do bạn muốn hủy đặt chỗ này:</p>
                  <select
                    className="cancel-select"
                    value={reasonCode}
                    onChange={(event) => setReasonCode(event.target.value)}
                  >
                    {customerCancelReasons.map(([code, label]) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {reasonCode === 'OTHER' && (
                    <textarea
                      className="cancel-textarea"
                      maxLength={500}
                      value={reasonDetails}
                      onChange={(event) => setReasonDetails(event.target.value)}
                      placeholder="Nhập lý do cụ thể (5–500 ký tự)"
                      rows={3}
                    />
                  )}
                  <div className="cancel-actions">
                    <button
                      type="button"
                      className="cancel-btn back-btn"
                      disabled={working}
                      onClick={() => setShowCancel(false)}
                    >
                      Quay lại
                    </button>
                    <button
                      type="button"
                      className="cancel-btn confirm-btn"
                      disabled={working}
                      onClick={cancel}
                    >
                      {working ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" /> Đang hủy...
                        </>
                      ) : (
                        'Xác nhận hủy'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showDriverAvatarModal && (
        <div
          className="customer-ride-modal-backdrop driver-photo-preview-backdrop"
          onClick={() => setShowDriverAvatarModal(false)}
        >
          <div
            className="driver-photo-preview-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Ảnh tài xế"
          >
            <div className="driver-photo-preview-header">
              <div className="driver-photo-preview-title">
                <i className="fa-solid fa-id-badge" />
                <span>Ảnh tài xế</span>
              </div>
              <button
                type="button"
                className="driver-photo-close-btn"
                onClick={() => setShowDriverAvatarModal(false)}
                aria-label="Đóng"
                title="Đóng (Esc)"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="driver-photo-preview-body">
              {driverAvatar ? (
                <div className="driver-photo-img-wrapper">
                  <img
                    src={driverAvatar}
                    alt={driverName || 'Tài xế EVG'}
                    className="driver-photo-preview-img"
                  />
                  <a
                    href={driverAvatar}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="driver-photo-full-btn"
                    title="Mở ảnh gốc trong tab mới"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square" /> Xem ảnh gốc
                  </a>
                </div>
              ) : (
                <div className="driver-photo-preview-empty">
                  <div className="empty-avatar-circle">
                    <i className="fa-regular fa-user" />
                  </div>
                  <strong>Chưa có ảnh đại diện</strong>
                  <p>Tài xế {driverName || 'EVG'} chưa tải lên ảnh hồ sơ.</p>
                </div>
              )}
            </div>

            <div className="driver-photo-preview-footer">
              <div className="driver-photo-meta">
                <strong className="driver-photo-name">{driverName || 'Tài xế EVG'}</strong>
                <div className="driver-photo-submeta">
                  {driverRating !== null && driverRating !== undefined && (
                    <span className="driver-rating-pill">
                      <i className="fa-solid fa-star" /> {Number(driverRating).toFixed(1)}
                    </span>
                  )}
                  {vehicleName && (
                    <span className="vehicle-pill">
                      <i className="fa-solid fa-car-side" /> {vehicleName}
                    </span>
                  )}
                  {vehiclePlate && (
                    <span className="plate-pill">{vehiclePlate}</span>
                  )}
                </div>
              </div>

              {driverPhone ? (
                <a
                  href={`tel:${driverPhone}`}
                  className="driver-photo-call-btn"
                  title={`Gọi tài xế: ${driverPhone}`}
                >
                  <i className="fa-solid fa-phone" />
                  <span>Gọi điện</span>
                </a>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </RidePortal>
  );
};

export default RideBookingDetailModal;
